import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import PortalFallbackPrint from './PortalFallbackPrint'

function todayStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

function timeToMinutes(t) {
  if (!t) return Infinity
  const [hm, ap] = t.split(' ')
  let [h, m] = hm.split(':').map(Number)
  if (ap === 'PM' && h !== 12) h += 12
  if (ap === 'AM' && h === 12) h = 0
  return h * 60 + (m || 0)
}

function fmtTime(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleTimeString('es-MX', { hour:'numeric', minute:'2-digit' })
}
function fmtDuration(inIso, outIso) {
  if (!inIso || !outIso) return null
  const mins = Math.round((new Date(outIso) - new Date(inIso)) / 60000)
  const h = Math.floor(mins/60), m = mins%60
  return h > 0 ? `${h}h ${m}min` : `${m}min`
}
function mapLink(lat, lng) {
  return `https://maps.google.com/?q=${lat},${lng}`
}

export default function TeamActivity() {
  const [subTab, setSubTab] = useState('activity') // 'activity' | 'live' | 'notes' | 'announcements' | 'equipment'
  const [showFallbackPrint, setShowFallbackPrint] = useState(false)
  const [date, setDate] = useState(todayStr())
  const [jobs, setJobs] = useState([])
  const [clients, setClients] = useState([])
  const [staff, setStaff] = useState([])
  const [timeLogs, setTimeLogs] = useState({})   // jobId -> [logs]
  const [estimates, setEstimates] = useState({}) // jobId -> row
  const [photoConfirmations, setPhotoConfirmations] = useState({}) // jobId -> row
  const [damageReports, setDamageReports] = useState({}) // jobId -> [rows]
  const [signoffs, setSignoffs] = useState({})   // jobId -> signoff
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchData() }, [date]) // eslint-disable-line

  async function fetchData() {
    setLoading(true)
    const [{ data: jb }, { data: cl }, { data: st }, { data: so }] = await Promise.all([
      supabase.from('jobs').select('*').eq('scheduled_date', date).eq('status', 'scheduled'),
      supabase.from('clients').select('*'),
      supabase.from('staff').select('*'),
      supabase.from('job_signoffs').select('*').eq('scheduled_date', date),
    ])
    setJobs(jb || [])
    setClients(cl || [])
    setStaff(st || [])
    const soMap = {}
    ;(so || []).forEach(x => { soMap[x.job_id] = x })
    setSignoffs(soMap)

    if (jb?.length) {
      const [{ data: tl }, { data: est }, { data: pc }, { data: dr }] = await Promise.all([
        supabase.from('time_logs').select('*').in('job_id', jb.map(j => j.id)),
        supabase.from('job_estimates').select('*').in('job_id', jb.map(j => j.id)),
        supabase.from('photo_confirmations').select('*').in('job_id', jb.map(j => j.id)),
        supabase.from('damage_reports').select('*').in('job_id', jb.map(j => j.id)),
      ])
      const tlMap = {}
      ;(tl || []).forEach(t => { (tlMap[t.job_id] = tlMap[t.job_id] || []).push(t) })
      setTimeLogs(tlMap)
      const estMap = {}
      ;(est || []).forEach(e => { (estMap[e.job_id] = estMap[e.job_id] || []).push(e) })
      setEstimates(estMap)
      const pcMap = {}
      ;(pc || []).forEach(p => { pcMap[p.job_id] = p })
      setPhotoConfirmations(pcMap)
      const drMap = {}
      ;(dr || []).forEach(d => { (drMap[d.job_id] = drMap[d.job_id] || []).push(d) })
      setDamageReports(drMap)
    } else {
      setTimeLogs({})
      setEstimates({})
      setPhotoConfirmations({})
      setDamageReports({})
    }
    setLoading(false)
  }

  async function approveEstimate(estimateId, approved) {
    await supabase.from('job_estimates').update({ approved }).eq('id', estimateId)
    fetchData()
  }

  async function correctEstimate(estimateId, correctedHours) {
    const hrs = parseFloat(correctedHours)
    if (isNaN(hrs)) return
    await supabase.from('job_estimates').update({
      estimated_hours: hrs, approved: true, reason: 'Corregido por Yosi'
    }).eq('id', estimateId)
    fetchData()
  }

  async function setStaffAlert(job, message) {
    await supabase.from('jobs').update({ staff_alert: message }).eq('id', job.id)
    fetchData()
  }

  async function addOneTimeItem(job, item) {
    const updated = [...(job.one_time_items||[]), item]
    await supabase.from('jobs').update({ one_time_items: updated }).eq('id', job.id)
    fetchData()
  }

  async function removeOneTimeItem(job, index) {
    const updated = (job.one_time_items||[]).filter((_,i)=>i!==index)
    await supabase.from('jobs').update({ one_time_items: updated }).eq('id', job.id)
    fetchData()
  }

  function staffName(id) { return staff.find(s => s.id === id)?.name || 'Desconocido' }
  function clientInfo(id) { return clients.find(c => c.id === id) }

  const routes = Array.from(new Set(jobs.filter(j => j.route).map(j => j.route))).sort()
  const noRouteJobs = jobs.filter(j => !j.route)

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div>
          <div style={s.title}>📡 Actividad del equipo</div>
          <div style={s.subtitle}>Clock in/out, ubicación, checklist, anuncios y equipo — desde el portal</div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={()=>setShowFallbackPrint(true)}
            style={{ padding:'7px 12px', border:'1px solid #e3e5e8', borderRadius:8, background:'#f8f9fa', fontSize:12, fontWeight:600, color:'#5f6368', cursor:'pointer' }}>
            🖨️ Hoja de respaldo
          </button>
          {subTab === 'activity' && <input type="date" value={date} onChange={e => setDate(e.target.value)} style={s.dateInput} />}
        </div>
      </div>

      <div style={s.subtabBar}>
        {[['activity','📡 Actividad'],['live','🔴 En vivo'],['notes','📝 Notas'],['announcements','📢 Anuncios'],['equipment','🧰 Equipo'],['checklists','✅ Checklists'],['hours','⏱️ Horas'],['weekly','📅 Semanal']].map(([id,label]) => (
          <button key={id} onClick={()=>setSubTab(id)}
            style={{...s.subtabBtn, ...(subTab===id ? s.subtabBtnActive : {})}}>
            {label}
          </button>
        ))}
      </div>

      {subTab === 'activity' && (
        <div style={s.body}>
          {loading ? (
            <div style={s.empty}>Cargando...</div>
          ) : jobs.length === 0 ? (
            <div style={s.empty}>Sin trabajos programados este día</div>
          ) : (
            <>
              {routes.map(r => (
                <RouteBlock key={r} label={`Ruta ${r}`} jobs={jobs.filter(j => j.route === r)}
                  clientInfo={clientInfo} staffName={staffName} timeLogs={timeLogs} signoffs={signoffs} estimates={estimates} onApprove={approveEstimate} onCorrect={correctEstimate}
                  photoConfirmations={photoConfirmations} damageReports={damageReports}
                  onAddOneTimeItem={addOneTimeItem} onRemoveOneTimeItem={removeOneTimeItem} onSetStaffAlert={setStaffAlert} />
              ))}
              {noRouteJobs.length > 0 && (
                <RouteBlock label="Sin ruta asignada" jobs={noRouteJobs}
                  clientInfo={clientInfo} staffName={staffName} timeLogs={timeLogs} signoffs={signoffs} estimates={estimates} onApprove={approveEstimate} onCorrect={correctEstimate}
                  photoConfirmations={photoConfirmations} damageReports={damageReports}
                  onAddOneTimeItem={addOneTimeItem} onRemoveOneTimeItem={removeOneTimeItem} onSetStaffAlert={setStaffAlert} />
              )}
            </>
          )}
        </div>
      )}

      {subTab === 'announcements' && <AnnouncementsPanel staff={staff} />}
      {subTab === 'equipment' && <EquipmentPanel staff={staff} />}
      {subTab === 'checklists' && <ChecklistPanel />}
      {subTab === 'hours' && <HoursPanel />}
      {subTab === 'weekly' && <WeeklySummaryPanel />}
      {subTab === 'live' && <LiveStatusPanel />}
      {subTab === 'notes' && <ClientNotesPanel />}
      {showFallbackPrint && <PortalFallbackPrint onClose={()=>setShowFallbackPrint(false)} />}
    </div>
  )
}

function RouteBlock({ label, jobs, clientInfo, staffName, timeLogs, signoffs, estimates, onApprove, onCorrect, photoConfirmations, damageReports, onAddOneTimeItem, onRemoveOneTimeItem, onSetStaffAlert }) {
  const [correctingId, setCorrectingId] = useState(null)
  const [correctionValue, setCorrectionValue] = useState('')
  const [oneTimeOpenId, setOneTimeOpenId] = useState(null)
  const [oneTimeInput, setOneTimeInput] = useState('')
  const [alertOpenId, setAlertOpenId] = useState(null)
  const [alertInput, setAlertInput] = useState('')
  const sorted = [...jobs].sort((a,b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time))
  return (
    <div style={s.routeBlock}>
      <div style={s.routeTitle}>{label}</div>
      {sorted.map(job => {
        const cl = clientInfo(job.client_id)
        const logs = timeLogs[job.id] || []
        const so = signoffs[job.id]
        const ests = estimates?.[job.id] || []
        const pc = photoConfirmations?.[job.id]
        const damages = damageReports?.[job.id] || []
        return (
          <div key={job.id} style={s.jobCard}>
            <div style={s.jobHead}>
              <div style={s.jobName}>{cl?.nickname || cl?.name || '?'}</div>
              <div style={s.jobTime}>{job.start_time || 'Todo el día'}</div>
            </div>

            {logs.length === 0 ? (
              <div style={s.noLog}>⏱️ Sin Clock In/Out todavía</div>
            ) : logs.map(log => (
              <div key={log.id} style={s.logRow}>
                <div style={s.logStaff}>{staffName(log.staff_id)}</div>
                <div style={s.logTimes}>
                  {fmtTime(log.clock_in) || '—'} → {log.clock_out ? fmtTime(log.clock_out) : 'en progreso'}
                  {log.clock_in && log.clock_out && <span style={s.duration}> · {fmtDuration(log.clock_in, log.clock_out)}</span>}
                </div>
                <div style={s.mapLinks}>
                  {log.clock_in_lat && (
                    <a href={mapLink(log.clock_in_lat, log.clock_in_lng)} target="_blank" rel="noreferrer" style={s.mapLink}>📍 Llegada</a>
                  )}
                  {log.clock_out_lat && (
                    <a href={mapLink(log.clock_out_lat, log.clock_out_lng)} target="_blank" rel="noreferrer" style={s.mapLink}>📍 Salida</a>
                  )}
                </div>
              </div>
            ))}

            {ests.length > 0 && (
              <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid #f1f3f4' }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#80868b', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:4 }}>
                  Tiempo · programado {job.hours}h
                </div>
                {ests.map(est => {
                  const changed = est.flag === 'over' || est.flag === 'under'
                  const color = est.flag==='over' ? '#c05050' : est.flag==='under' ? '#92700e' : '#1d9e75'
                  return (
                    <div key={est.id} style={{ background: changed ? (est.flag==='over' ? '#fff3f3' : '#fff8e1') : 'transparent', borderRadius:8, padding: changed ? '7px 9px' : '3px 0', marginBottom:4 }}>
                      <div style={{ fontSize:12, color }}>
                        <b style={{ color:'#202124' }}>{staffName(est.staff_id)}:</b>{' '}
                        {est.response === 'ok' || (!changed && !est.response)
                          ? <>✓ {est.estimated_hours}h está bien</>
                          : <>pide <b>{est.estimated_hours}h</b> ({est.flag==='over' ? '+' : ''}{(parseFloat(est.estimated_hours) - parseFloat(est.scheduled_hours || job.hours)).toFixed(1).replace('.0','')}h)</>}
                      </div>
                      {(est.tags?.length > 0 || est.reason) && est.reason !== 'Corregido por Yosi' && (
                        <div style={{ fontSize:11, color:'#5f6368', marginTop:2 }}>“{[...(est.tags||[]), est.reason].filter(Boolean).join(', ')}”</div>
                      )}
                      {est.photo_url && (
                        <a href={est.photo_url} target="_blank" rel="noreferrer" style={{ fontSize:11, color:'#185fa5', fontWeight:600 }}>📷 Ver foto</a>
                      )}
                      {changed && (est.approved ? (
                        <div style={{ fontSize:11, fontWeight:700, color:'#1d9e75', marginTop:4 }}>✓ Aprobado{est.reason==='Corregido por Yosi'?' (corregido)':''}</div>
                      ) : est.approved===false ? (
                        <div style={{ fontSize:11, fontWeight:700, color:'#c05050', marginTop:4 }}>✕ Rechazado</div>
                      ) : correctingId === est.id ? (
                        <div style={{ marginTop:6, padding:'8px', background:'#fff', borderRadius:8, border:'1px solid #f4b8b8' }}>
                          <div style={{ fontSize:11, fontWeight:600, color:'#c05050', marginBottom:6 }}>¿Cuántas horas apruebas en su lugar?</div>
                          <div style={{ display:'flex', gap:6 }}>
                            <input type="number" step="0.5" min="0" value={correctionValue} onChange={e=>setCorrectionValue(e.target.value)}
                              style={{ width:60, padding:'6px 8px', border:'1px solid #e8547a', borderRadius:6, fontSize:12, fontWeight:700 }} />
                            <button onClick={()=>{ onCorrect(est.id, correctionValue); setCorrectingId(null) }}
                              style={{ flex:1, padding:'6px', border:'none', borderRadius:6, background:'#1d9e75', color:'#fff', fontWeight:700, fontSize:11, cursor:'pointer' }}>
                              ✓ Aprobar con esta corrección
                            </button>
                            <button onClick={()=>setCorrectingId(null)}
                              style={{ padding:'6px 10px', border:'1px solid #e3e5e8', borderRadius:6, background:'#fff', color:'#80868b', fontSize:11, cursor:'pointer' }}>
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display:'flex', gap:6, marginTop:6 }}>
                          <button onClick={()=>onApprove(est.id, true)} style={{ flex:1, padding:'6px', border:'none', borderRadius:6, background:'#1d9e75', color:'#fff', fontWeight:700, fontSize:11, cursor:'pointer' }}>
                            ✓ Aprobar {est.estimated_hours}h
                          </button>
                          <button onClick={()=>{ setCorrectingId(est.id); setCorrectionValue(String(est.scheduled_hours || job.hours || '')) }}
                            style={{ padding:'6px 10px', border:'1px solid #e3e5e8', borderRadius:6, background:'#fff', color:'#80868b', fontWeight:600, fontSize:11, cursor:'pointer' }}>
                            Otra cantidad
                          </button>
                        </div>
                      ))}
                    </div>
                  )
                })}
              </div>
            )}

            <div style={s.signoffRow}>
              {so ? (
                <span style={s.signedBadge}>✓ Firmado por {so.initials} · {so.checked_items?.length || 0} tareas</span>
              ) : (
                <span style={s.unsignedBadge}>Sin firmar todavía</span>
              )}
              {so?.comments && (
                <div style={{ fontSize:11, color:'#5f6368', marginTop:6, padding:'6px 8px', background:'#f1f3f4', borderRadius:6 }}>
                  📋 Nota para el próximo equipo: {so.comments}
                </div>
              )}
            </div>

            {(pc?.before_sent || pc?.after_sent) && (
              <div style={{ display:'flex', gap:6, marginTop:6 }}>
                <span style={{ fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:99, background: pc?.before_sent?'#e1f5ee':'#f1f3f4', color: pc?.before_sent?'#1d9e75':'#9aa0a6' }}>
                  📸 Antes {pc?.before_sent?'✓':'pendiente'}
                </span>
                <span style={{ fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:99, background: pc?.after_sent?'#e1f5ee':'#f1f3f4', color: pc?.after_sent?'#1d9e75':'#9aa0a6' }}>
                  📸 Después {pc?.after_sent?'✓':'pendiente'}
                </span>
              </div>
            )}

            {damages.length > 0 && damages.map(d => (
              <div key={d.id} style={{ marginTop:6, padding:'8px 10px', background:'#fff3f3', border:'1px solid #f4b8b8', borderRadius:8 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#c05050', marginBottom:2 }}>⚠️ Reportado por {staffName(d.staff_id)}</div>
                <div style={{ fontSize:12, color:'#5a1a1a' }}>{d.explanation}</div>
                {d.photo_url && <a href={d.photo_url} target="_blank" rel="noreferrer" style={{ fontSize:11, color:'#185fa5', fontWeight:600 }}>📷 Ver foto</a>}
              </div>
            ))}

            {so?.missing_supplies && (
              <div style={{ marginTop:6, padding:'8px 10px', background:'#fff8e1', border:'1px solid #ffe082', borderRadius:8 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#854f0b', marginBottom:2 }}>📦 Relleno faltante</div>
                <div style={{ fontSize:12, color:'#5a4000' }}>{so.missing_supplies}</div>
              </div>
            )}

            <div style={{ marginTop:8 }}>
              {job.staff_alert ? (
                <div style={{ padding:'8px 10px', background:'#fff8e1', border:'1px solid #ffe082', borderRadius:8, marginBottom:6 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', gap:6 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#854f0b' }}>⚠️ Aviso que verán al llegar</div>
                    <button onClick={()=>onSetStaffAlert(job,null)} style={{ border:'none', background:'none', color:'#bdc1c6', cursor:'pointer', fontSize:12 }}>×</button>
                  </div>
                  <div style={{ fontSize:12, color:'#5a4000', marginTop:2 }}>{job.staff_alert}</div>
                </div>
              ) : alertOpenId === job.id ? (
                <div style={{ display:'flex', gap:6, marginBottom:6 }}>
                  <input value={alertInput} onChange={e=>setAlertInput(e.target.value)} placeholder="Ej: cambio de planes, hoy también..."
                    onKeyDown={e=>{ if(e.key==='Enter' && alertInput.trim()){ onSetStaffAlert(job, alertInput.trim()); setAlertInput(''); setAlertOpenId(null) } }}
                    style={{ flex:1, padding:'5px 8px', border:'1px solid #ffe082', borderRadius:6, fontSize:11 }} />
                  <button onClick={()=>{ if(alertInput.trim()){ onSetStaffAlert(job, alertInput.trim()); setAlertInput(''); setAlertOpenId(null) } }}
                    style={{ padding:'5px 10px', border:'none', borderRadius:6, background:'#f59e0b', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>+</button>
                  <button onClick={()=>{ setAlertOpenId(null); setAlertInput('') }}
                    style={{ padding:'5px 8px', border:'none', background:'none', color:'#80868b', fontSize:11, cursor:'pointer' }}>×</button>
                </div>
              ) : (
                <button onClick={()=>setAlertOpenId(job.id)}
                  style={{ fontSize:11, color:'#f59e0b', border:'none', background:'none', cursor:'pointer', fontWeight:600, padding:0, display:'block', marginBottom:6 }}>
                  ⚠️ Agregar aviso importante para esta visita
                </button>
              )}
            </div>

            <div style={{ marginTop:8 }}>
              {(job.one_time_items||[]).length > 0 && (
                <div style={{ marginBottom:6 }}>
                  {job.one_time_items.map((item,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'#5f6368', padding:'3px 0' }}>
                      <span style={{ flex:1 }}>• {item}</span>
                      <button onClick={()=>onRemoveOneTimeItem(job,i)} style={{ border:'none', background:'none', color:'#bdc1c6', cursor:'pointer', fontSize:12 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {oneTimeOpenId === job.id ? (
                <div style={{ display:'flex', gap:6 }}>
                  <input value={oneTimeInput} onChange={e=>setOneTimeInput(e.target.value)} placeholder="Nuevo item para hoy..."
                    onKeyDown={e=>{ if(e.key==='Enter' && oneTimeInput.trim()){ onAddOneTimeItem(job, oneTimeInput.trim()); setOneTimeInput('') } }}
                    style={{ flex:1, padding:'5px 8px', border:'1px solid #e3e5e8', borderRadius:6, fontSize:11 }} />
                  <button onClick={()=>{ if(oneTimeInput.trim()){ onAddOneTimeItem(job, oneTimeInput.trim()); setOneTimeInput('') } }}
                    style={{ padding:'5px 10px', border:'none', borderRadius:6, background:'#185fa5', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>+</button>
                  <button onClick={()=>{ setOneTimeOpenId(null); setOneTimeInput('') }}
                    style={{ padding:'5px 8px', border:'none', background:'none', color:'#80868b', fontSize:11, cursor:'pointer' }}>×</button>
                </div>
              ) : (
                <button onClick={()=>setOneTimeOpenId(job.id)}
                  style={{ fontSize:11, color:'#185fa5', border:'none', background:'none', cursor:'pointer', fontWeight:600, padding:0 }}>
                  + Agregar item único para hoy
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const s = {
  wrap: { display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' },
  header: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:'1px solid #e3e5e8', background:'#fff', flexShrink:0 },
  title: { fontSize:17, fontWeight:700, color:'#202124' },
  subtitle: { fontSize:12, color:'#80868b', marginTop:2 },
  dateInput: { padding:'7px 10px', border:'1px solid #e3e5e8', borderRadius:8, fontSize:13, background:'#f8f9fa' },
  subtabBar: { display:'flex', gap:6, padding:'10px 20px 0', background:'#fff', borderBottom:'1px solid #e3e5e8', flexShrink:0 },
  subtabBtn: { padding:'8px 14px', border:'none', background:'none', fontSize:12, fontWeight:600, color:'#80868b', cursor:'pointer', borderBottom:'2px solid transparent' },
  subtabBtnActive: { color:'#e8547a', borderBottom:'2px solid #e8547a' },
  body: { flex:1, overflowY:'auto', padding:'16px 20px', background:'#f8f9fa' },
  empty: { textAlign:'center', color:'#9aa0a6', padding:60, fontSize:14 },
  routeBlock: { marginBottom:22 },
  routeTitle: { fontSize:13, fontWeight:700, color:'#e8547a', marginBottom:8, textTransform:'uppercase', letterSpacing:'.04em' },
  jobCard: { background:'#fff', borderRadius:12, border:'1px solid #e3e5e8', padding:'12px 14px', marginBottom:10 },
  jobHead: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 },
  jobName: { fontSize:14, fontWeight:700, color:'#202124' },
  jobTime: { fontSize:12, color:'#80868b' },
  noLog: { fontSize:12, color:'#9aa0a6', padding:'4px 0' },
  logRow: { display:'flex', alignItems:'center', gap:10, padding:'6px 0', borderTop:'1px solid #f1f3f4', flexWrap:'wrap' },
  logStaff: { fontSize:12, fontWeight:600, color:'#333', minWidth:90 },
  logTimes: { fontSize:12, color:'#5f6368', flex:1, minWidth:160 },
  duration: { color:'#1d9e75', fontWeight:600 },
  mapLinks: { display:'flex', gap:8 },
  mapLink: { fontSize:11, color:'#185fa5', textDecoration:'none', fontWeight:600 },
  signoffRow: { marginTop:8, paddingTop:8, borderTop:'1px solid #f1f3f4' },
  signedBadge: { fontSize:11, fontWeight:600, color:'#1d9e75', background:'#e1f5ee', padding:'3px 9px', borderRadius:99 },
  unsignedBadge: { fontSize:11, fontWeight:600, color:'#80868b', background:'#f1f3f4', padding:'3px 9px', borderRadius:99 },
}

function AnnouncementsPanel({ staff }) {
  const [announcements, setAnnouncements] = useState([])
  const [reactions, setReactions] = useState({}) // announcementId -> [{staff_id, emoji}]
  const [message, setMessage] = useState('')
  const [targetIds, setTargetIds] = useState([]) // [] = everyone
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => { load() }, []) // eslint-disable-line

  async function load() {
    setLoading(true)
    const { data: ann } = await supabase.from('staff_announcements').select('*').order('created_at', { ascending:false }).limit(30)
    setAnnouncements(ann || [])
    if (ann?.length) {
      const { data: rx } = await supabase.from('announcement_reactions').select('*').in('announcement_id', ann.map(a=>a.id))
      const map = {}
      ;(rx||[]).forEach(r => { (map[r.announcement_id] = map[r.announcement_id] || []).push(r) })
      setReactions(map)
    }
    setLoading(false)
  }

  async function send() {
    if (!message.trim()) return
    setSending(true)
    await supabase.from('staff_announcements').insert({ body: message.trim(), target_staff_ids: targetIds.length ? targetIds : null })
    setMessage('')
    setTargetIds([])
    setSending(false)
    load()
  }

  async function deleteAnnouncement(id) {
    if (!window.confirm('¿Eliminar este anuncio?')) return
    await supabase.from('staff_announcements').delete().eq('id', id)
    load()
  }

  function toggleTarget(id) {
    setTargetIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id])
  }

  function staffName(id) { return staff.find(s=>s.id===id)?.name || '?' }
  const activeStaff = staff.filter(st => st.is_active !== false)

  return (
    <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', background:'#f8f9fa' }}>
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e3e5e8', padding:14, marginBottom:18 }}>
        <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>🐾 Nuevo anuncio (Yosi Bot)</div>
        <textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Escribe el anuncio..."
          style={{ width:'100%', padding:10, border:'1px solid #e3e5e8', borderRadius:8, fontSize:13, resize:'none', height:64, fontFamily:'inherit', marginBottom:10 }} />
        <div style={{ fontSize:11, fontWeight:700, color:'#80868b', marginBottom:6 }}>
          {targetIds.length===0 ? '📢 Para todos (elige personas para enviar solo a ellas)' : `👤 Enviando solo a ${targetIds.length} persona(s)`}
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
          {activeStaff.map(st => (
            <button key={st.id} onClick={()=>toggleTarget(st.id)}
              style={{ padding:'6px 12px', borderRadius:99, border:'1px solid #e3e5e8', fontSize:12, fontWeight:600, cursor:'pointer',
                background: targetIds.includes(st.id) ? '#185fa5' : '#fff', color: targetIds.includes(st.id) ? '#fff' : '#5f6368' }}>
              {st.name}
            </button>
          ))}
        </div>
        <button onClick={send} disabled={sending||!message.trim()}
          style={{ width:'100%', padding:'9px 18px', border:'none', borderRadius:8, background:'#e8547a', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
          {sending?'...':'Enviar'}
        </button>
      </div>

      {loading ? <div style={{textAlign:'center',color:'#9aa0a6',padding:30}}>Cargando...</div> : announcements.length===0 ? (
        <div style={{textAlign:'center',color:'#9aa0a6',padding:30}}>Sin anuncios todavía</div>
      ) : announcements.map(a => {
        const rx = reactions[a.id] || []
        const targets = a.target_staff_ids?.length ? a.target_staff_ids : (a.target_staff_id ? [a.target_staff_id] : [])
        const targetNames = targets.map(staffName)
        const expectedStaff = targets.length ? targets : activeStaff.map(s=>s.id)
        const seenIds = new Set(rx.map(r=>r.staff_id))
        const notSeen = expectedStaff.filter(id => !seenIds.has(id))
        return (
          <div key={a.id} style={{ background:'#fff', borderRadius:12, border:'1px solid #e3e5e8', padding:'12px 14px', marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
              <div style={{ fontSize:10, fontWeight:700, color: targetNames.length?'#185fa5':'#e8547a' }}>
                {targetNames.length ? `👤 ${targetNames.join(', ')}` : '📢 Para todos'} · {new Date(a.created_at).toLocaleString('es-MX',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}
              </div>
              <button onClick={()=>deleteAnnouncement(a.id)} style={{ border:'none', background:'none', color:'#bdc1c6', cursor:'pointer', fontSize:14, padding:0 }}>🗑️</button>
            </div>
            <div style={{ fontSize:14, color:'#202124', marginBottom:8 }}>{a.body}</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {rx.map(r => (
                <span key={r.staff_id} style={{ fontSize:11, background:'#e1f5ee', color:'#1d9e75', padding:'2px 8px', borderRadius:99, fontWeight:600 }}>
                  {r.emoji} {staffName(r.staff_id)}
                </span>
              ))}
              {notSeen.map(id => (
                <span key={id} style={{ fontSize:11, background:'#f1f3f4', color:'#9aa0a6', padding:'2px 8px', borderRadius:99 }}>
                  ⏳ {staffName(id)}
                </span>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function EquipmentPanel({ staff }) {
  const [items, setItems] = useState([])
  const [newItem, setNewItem] = useState('')
  const [newCategory, setNewCategory] = useState('Aspiradora')
  const [date, setDate] = useState(todayStr())
  const [checkouts, setCheckouts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { load() }, [date]) // eslint-disable-line

  async function load() {
    setLoading(true)
    const [{ data: it }, { data: co }] = await Promise.all([
      supabase.from('equipment_items').select('*').order('category').order('name'),
      supabase.from('equipment_checkouts').select('*').eq('date', date),
    ])
    setItems(it || [])
    setCheckouts(co || [])
    setLoading(false)
  }

  async function addItem() {
    if (!newItem.trim()) return
    await supabase.from('equipment_items').insert({ name: newItem.trim(), category: newCategory })
    setNewItem('')
    load()
  }

  async function toggleActive(item) {
    await supabase.from('equipment_items').update({ is_active: !item.is_active }).eq('id', item.id)
    load()
  }

  function staffName(id) { return staff.find(s=>s.id===id)?.name || '?' }
  function itemName(id) { return items.find(i=>i.id===id)?.name || '?' }
  const flagged = checkouts.filter(c => c.issue_note)
  const categories = Array.from(new Set(items.map(it=>it.category||'General')))
  const staffIdsToday = Array.from(new Set(checkouts.map(c=>c.staff_id)))

  return (
    <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', background:'#f8f9fa' }}>
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e3e5e8', padding:14, marginBottom:18 }}>
        <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>🧰 Equipo disponible</div>
        {categories.map(cat => (
          <div key={cat} style={{ marginBottom:10 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#80868b', marginBottom:4 }}>{cat}</div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {items.filter(it=>(it.category||'General')===cat).map(it => (
                <div key={it.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background:'#f1f3f4', borderRadius:8 }}>
                  <span style={{ fontSize:13, color: it.is_active?'#333':'#9aa0a6', textDecoration: it.is_active?'none':'line-through' }}>{it.name}</span>
                  <button onClick={()=>toggleActive(it)} style={{ fontSize:11, border:'none', background:'none', color:'#185fa5', cursor:'pointer', fontWeight:600 }}>
                    {it.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div style={{ display:'flex', gap:8, marginTop:8 }}>
          <select value={newCategory} onChange={e=>setNewCategory(e.target.value)}
            style={{ padding:'8px 10px', border:'1px solid #e3e5e8', borderRadius:8, fontSize:13, background:'#f8f9fa' }}>
            {['Aspiradora','Trapos','Kit de limpieza','General'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={newItem} onChange={e=>setNewItem(e.target.value)} placeholder="Nuevo (ej: Aspiradora E)"
            style={{ flex:1, padding:'8px 10px', border:'1px solid #e3e5e8', borderRadius:8, fontSize:13 }} />
          <button onClick={addItem} style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#185fa5', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
            + Agregar
          </button>
        </div>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ fontSize:13, fontWeight:700 }}>Quién se llevó qué</div>
        <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{ padding:'6px 10px', border:'1px solid #e3e5e8', borderRadius:8, fontSize:12 }} />
      </div>

      {loading ? <div style={{textAlign:'center',color:'#9aa0a6',padding:20}}>Cargando...</div> : staffIdsToday.length===0 ? (
        <div style={{textAlign:'center',color:'#9aa0a6',padding:20}}>Nadie ha registrado equipo este día</div>
      ) : (
        <div style={{ marginBottom:18 }}>
          {staffIdsToday.map(sid => {
            const mine = checkouts.filter(c=>c.staff_id===sid)
            return (
              <div key={sid} style={{ background:'#fff', borderRadius:10, border:'1px solid #e3e5e8', padding:'10px 12px', marginBottom:8 }}>
                <div style={{ fontSize:13, fontWeight:700, marginBottom:4 }}>{staffName(sid)}</div>
                <div style={{ fontSize:12, color:'#5f6368' }}>{mine.map(c=>itemName(c.equipment_item_id)).join(', ')}</div>
                {mine.filter(c=>c.issue_note).map(c => (
                  <div key={c.id} style={{ fontSize:12, color:'#c05050', marginTop:4 }}>⚠️ {itemName(c.equipment_item_id)}: {c.issue_note}</div>
                ))}
              </div>
            )
          })}
        </div>
      )}

      {flagged.length > 0 && (
        <div style={{ background:'#fff3f3', border:'1px solid #f4b8b8', borderRadius:10, padding:'10px 12px' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#c05050', marginBottom:4 }}>⚠️ {flagged.length} problema(s) reportado(s) hoy</div>
        </div>
      )}
    </div>
  )
}

const STEP_LABELS = {
  assign_walkthrough: '🙋 Decidiendo quién hace el recorrido',
  waiting_walkthrough: '⏳ Esperando a que otra persona haga el recorrido',
  walkthrough: '🚶 Haciendo recorrido de la casa',
  estimate: '🤔 Estimando cuánto va a tomar',
  estimate_flag: '📸 Explicando por qué va a tardar más',
  time_info: '⏱️ Viendo tiempo calculado (sin estimar)',
  awaiting_approval: '📲 Esperando tu aprobación del tiempo',
  checklist_final: '✅ Terminando el checklist',
  traveling: '🚗 En camino a la siguiente casa',
  clocked_out: '🏁 Salió de la última casa',
}

function LiveStatusPanel() {
  const [rows, setRows] = useState([])
  const [staff, setStaff] = useState([])
  const [clients, setClients] = useState([])
  const [jobs, setJobs] = useState([])
  const [estimates, setEstimates] = useState({})
  const [equipment, setEquipment] = useState({}) // staffId -> [item names]
  const [timeLogs, setTimeLogs] = useState({})   // jobId+staffId -> log
  const [loading, setLoading] = useState(true)
  const today = todayStr()

  useEffect(() => {
    load()
    const interval = setInterval(load, 30000) // refresh every 30s — not truly live, but close
    return () => clearInterval(interval)
  }, []) // eslint-disable-line

  async function load() {
    const [{ data: st }, { data: cl }, { data: jb }, { data: sds }, { data: eq }, { data: co }] = await Promise.all([
      supabase.from('staff').select('*').eq('is_active', true),
      supabase.from('clients').select('*'),
      supabase.from('jobs').select('*').eq('scheduled_date', today),
      supabase.from('staff_day_status').select('*').eq('date', today),
      supabase.from('equipment_items').select('*'),
      supabase.from('equipment_checkouts').select('*').eq('date', today),
    ])
    setStaff(st||[])
    setClients(cl||[])
    setJobs(jb||[])
    setRows(sds||[])

    const eqMap = {}
    ;(co||[]).forEach(c => {
      const name = (eq||[]).find(i=>i.id===c.equipment_item_id)?.name
      if (name) (eqMap[c.staff_id] = eqMap[c.staff_id] || []).push(name)
    })
    setEquipment(eqMap)

    if (jb?.length) {
      const [{ data: est }, { data: tl }] = await Promise.all([
        supabase.from('job_estimates').select('*').in('job_id', jb.map(j=>j.id)),
        supabase.from('time_logs').select('*').in('job_id', jb.map(j=>j.id)),
      ])
      const estMap = {}
      ;(est||[]).forEach(e => { estMap[e.job_id+'|'+e.staff_id] = e })
      setEstimates(estMap)
      const tlMap = {}
      ;(tl||[]).forEach(t => { tlMap[t.job_id+'|'+t.staff_id] = t })
      setTimeLogs(tlMap)
    }
    setLoading(false)
  }

  function staffName(id) { return staff.find(s=>s.id===id)?.name || 'Desconocido' }
  function jobClientName(jobId) {
    const job = jobs.find(j=>j.id===jobId)
    if (!job) return '?'
    return clients.find(c=>c.id===job.client_id)?.nickname || clients.find(c=>c.id===job.client_id)?.name || '?'
  }
  function jobRoute(jobId) { return jobs.find(j=>j.id===jobId)?.route || '?' }
  function minutesAgo(iso) {
    return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  }

  const sorted = [...rows].sort((a,b) => new Date(b.updated_at) - new Date(a.updated_at))

  return (
    <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', background:'#f8f9fa' }}>
      <div style={{ fontSize:11, color:'#aaa', marginBottom:12 }}>Se actualiza cada 30 segundos — no es al instante, pero casi.</div>
      {loading ? (
        <div style={{ textAlign:'center', color:'#9aa0a6', padding:30 }}>Cargando...</div>
      ) : sorted.length === 0 ? (
        <div style={{ textAlign:'center', color:'#9aa0a6', padding:30 }}>Nadie ha empezado su día todavía</div>
      ) : sorted.map(r => {
        const ago = minutesAgo(r.updated_at)
        const est = estimates[r.job_id+'|'+r.staff_id]
        const log = timeLogs[r.job_id+'|'+r.staff_id]
        const myEquipment = equipment[r.staff_id] || []
        return (
          <div key={r.id} style={{ background:'#fff', borderRadius:12, border:'1px solid #e3e5e8', padding:'12px 14px', marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4 }}>
              <div style={{ fontSize:14, fontWeight:700, color:'#202124' }}>{staffName(r.staff_id)}</div>
              <div style={{ fontSize:11, color: ago>20 ? '#c05050' : '#9aa0a6' }}>hace {ago} min</div>
            </div>
            <div style={{ fontSize:13, color:'#5f6368', marginBottom:2 }}>🏠 {jobClientName(r.job_id)} · Ruta {jobRoute(r.job_id)}</div>
            <div style={{ fontSize:13, color:'#333', fontWeight:600, marginBottom:2 }}>{STEP_LABELS[r.step] || r.step}</div>
            {log?.clock_in && (
              <div style={{ fontSize:11, color:'#80868b' }}>Llegó a las {fmtTime(log.clock_in)}{log.clock_out?` · salió ${fmtTime(log.clock_out)}`:''}</div>
            )}
            {est && (
              <div style={{ fontSize:11, color: est.flag==='over'?'#c05050':est.flag==='under'?'#92700e':'#1d9e75', marginTop:2 }}>
                ⏱️ {est.response==='ok' ? `Confirmó ${est.estimated_hours}h` : `Pide ${est.estimated_hours}h (programado ${est.scheduled_hours}h)`}{est.flag!=='ok' && est.flag ? (est.approved?' · aprobado':est.approved===false?' · rechazado':' · esperando tu respuesta') : ''}
              </div>
            )}
            {myEquipment.length > 0 && (
              <div style={{ fontSize:11, color:'#80868b', marginTop:2 }}>🧰 {myEquipment.join(', ')}</div>
            )}
            {r.note && <div style={{ fontSize:12, color:'#185fa5', marginTop:4 }}>💬 {r.note}</div>}
          </div>
        )
      })}
    </div>
  )
}

function ClientNotesPanel() {
  const [notes, setNotes] = useState([])
  const [clients, setClients] = useState([])
  const [staff, setStaff] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { load() }, []) // eslint-disable-line

  async function load() {
    setLoading(true)
    const [{ data: n }, { data: cl }, { data: st }] = await Promise.all([
      supabase.from('client_note_submissions').select('*').order('created_at', { ascending:false }),
      supabase.from('clients').select('*'),
      supabase.from('staff').select('*'),
    ])
    setNotes(n||[])
    setClients(cl||[])
    setStaff(st||[])
    setLoading(false)
  }

  function clientName(id) { return clients.find(c=>c.id===id)?.nickname || clients.find(c=>c.id===id)?.name || '?' }
  function staffName(id) { return staff.find(s=>s.id===id)?.name || '?' }

  async function approve(note) {
    const client = clients.find(c=>c.id===note.client_id)
    const updated = client?.notes ? `${client.notes}\n\n${note.note}` : note.note
    await supabase.from('clients').update({ notes: updated }).eq('id', note.client_id)
    await supabase.from('client_note_submissions').update({ status:'approved' }).eq('id', note.id)
    load()
  }

  async function reject(note) {
    await supabase.from('client_note_submissions').update({ status:'rejected' }).eq('id', note.id)
    load()
  }

  const pending = notes.filter(n=>n.status==='pending')
  const resolved = notes.filter(n=>n.status!=='pending').slice(0,10)

  return (
    <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', background:'#f8f9fa' }}>
      <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>📝 Pendientes de aprobar ({pending.length})</div>
      {loading ? (
        <div style={{ textAlign:'center', color:'#9aa0a6', padding:20 }}>Cargando...</div>
      ) : pending.length === 0 ? (
        <div style={{ textAlign:'center', color:'#9aa0a6', padding:20 }}>Nada pendiente ahora mismo</div>
      ) : pending.map(n => (
        <div key={n.id} style={{ background:'#fff', borderRadius:12, border:'1px solid #e3e5e8', padding:'12px 14px', marginBottom:10 }}>
          <div style={{ fontSize:11, color:'#80868b', marginBottom:4 }}>{clientName(n.client_id)} · de {staffName(n.staff_id)}</div>
          <div style={{ fontSize:14, color:'#202124', marginBottom:10 }}>{n.note}</div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>approve(n)} style={{ flex:1, padding:'8px', border:'none', borderRadius:8, background:'#1d9e75', color:'#fff', fontWeight:700, fontSize:12, cursor:'pointer' }}>
              ✓ Aprobar y agregar al perfil
            </button>
            <button onClick={()=>reject(n)} style={{ padding:'8px 14px', border:'1px solid #e3e5e8', borderRadius:8, background:'#fff', color:'#80868b', fontWeight:600, fontSize:12, cursor:'pointer' }}>
              Descartar
            </button>
          </div>
        </div>
      ))}

      {resolved.length > 0 && (
        <>
          <div style={{ fontSize:12, fontWeight:700, color:'#9aa0a6', marginTop:18, marginBottom:8 }}>Resueltas recientemente</div>
          {resolved.map(n => (
            <div key={n.id} style={{ fontSize:12, color:'#80868b', padding:'6px 0', borderBottom:'1px solid #e3e5e8' }}>
              {n.status==='approved'?'✓':'✕'} {clientName(n.client_id)}: {n.note}
            </div>
          ))}
        </>
      )}
    </div>
  )
}

const CHECKLIST_TYPE_LABELS = {
  regular: '🧹 Regular',
  deep: '🫧 Deep Clean',
  airbnb: '🏠 Airbnb Turnover',
  moveout: '📦 Move-in/Move-out',
  janitorial: '🏢 Janitorial',
}

function ChecklistPanel() {
  const [checklists, setChecklists] = useState({})
  const [loading, setLoading] = useState(true)
  const [editingType, setEditingType] = useState('regular')
  const [newItem, setNewItem] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => { load() }, []) // eslint-disable-line

  async function load() {
    setLoading(true)
    const { data } = await supabase.from('checklist_templates').select('*')
    const map = {}
    ;(data||[]).forEach(d => { map[d.clean_type] = d.items })
    setChecklists(map)
    setLoading(false)
  }

  async function saveChecklist() {
    setSaving(true)
    await supabase.from('checklist_templates').upsert({
      clean_type: editingType,
      items: checklists[editingType] || [],
      updated_at: new Date().toISOString()
    }, { onConflict: 'clean_type' })
    setSaving(false)
  }

  function addItem() {
    if (!newItem.trim()) return
    setChecklists(prev => ({ ...prev, [editingType]: [...(prev[editingType]||[]), newItem.trim()] }))
    setNewItem('')
  }

  function removeItem(i) {
    setChecklists(prev => ({ ...prev, [editingType]: (prev[editingType]||[]).filter((_,idx)=>idx!==i) }))
  }

  function moveItem(i, dir) {
    const items = [...(checklists[editingType]||[])]
    const j = i + dir
    if (j < 0 || j >= items.length) return
    ;[items[i], items[j]] = [items[j], items[i]]
    setChecklists(prev => ({ ...prev, [editingType]: items }))
  }

  return (
    <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', background:'#f8f9fa' }}>
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e3e5e8', padding:14 }}>
        <div style={{ fontSize:13, fontWeight:700, marginBottom:4 }}>✅ Listas de limpieza</div>
        <div style={{ fontSize:11, color:'#9aa0a6', marginBottom:12 }}>Edita las listas que ven las empleadas en su portal. Los cambios se guardan al instante para todos.</div>

        <div style={{ display:'flex', gap:4, marginBottom:14, flexWrap:'wrap' }}>
          {Object.entries(CHECKLIST_TYPE_LABELS).map(([type,label]) => (
            <button key={type} onClick={()=>setEditingType(type)}
              style={{ fontSize:11, padding:'5px 10px', borderRadius:8, border:'1px solid #e3e5e8', cursor:'pointer',
                background: editingType===type?'#e8547a':'#fff', color: editingType===type?'#fff':'#5f6368', fontWeight: editingType===type?600:400 }}>
              {label}
            </button>
          ))}
        </div>

        {loading ? <div style={{ color:'#9aa0a6', fontSize:12 }}>Cargando...</div> : (
          <>
            <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:10 }}>
              {(checklists[editingType]||[]).map((item,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 10px', background:'#f8f9fa', borderRadius:8, fontSize:12 }}>
                  <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
                    <button style={{ fontSize:9, border:'none', background:'none', cursor:'pointer', color:'#9aa0a6', lineHeight:1 }} onClick={()=>moveItem(i,-1)}>▲</button>
                    <button style={{ fontSize:9, border:'none', background:'none', cursor:'pointer', color:'#9aa0a6', lineHeight:1 }} onClick={()=>moveItem(i,1)}>▼</button>
                  </div>
                  <div style={{ flex:1, color:'#333' }}>{item}</div>
                  <button style={{ fontSize:13, border:'none', background:'none', color:'#ddd', cursor:'pointer' }} onClick={()=>removeItem(i)}>×</button>
                </div>
              ))}
              {(checklists[editingType]||[]).length===0 && (
                <div style={{ fontSize:12, color:'#9aa0a6', padding:'10px 0' }}>Sin items todavía para este tipo.</div>
              )}
            </div>
            <div style={{ display:'flex', gap:6, marginBottom:10 }}>
              <input style={{ flex:1, padding:'7px 9px', border:'1px solid #e3e5e8', borderRadius:8, fontSize:12, background:'#f8f9fa' }}
                value={newItem} placeholder="Agregar nuevo item..."
                onChange={e=>setNewItem(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&addItem()} />
              <button style={{ padding:'7px 12px', border:'none', borderRadius:8, background:'#e8547a', color:'#fff', cursor:'pointer', fontSize:12 }}
                onClick={addItem}>+ Agregar</button>
            </div>
            <button style={{ width:'100%', padding:'8px', border:'none', borderRadius:8, background:'#1d9e75', color:'#fff', fontWeight:600, cursor:'pointer', fontSize:12 }}
              onClick={saveChecklist} disabled={saving}>
              {saving?'Guardando...':'💾 Guardar lista'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

function HoursPanel() {
  const [date, setDate] = useState(todayStr())
  const [rows, setRows] = useState([]) // [{staffId, name, calculated, approved, clocked, finalHours}]
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState({}) // staffId -> bool

  useEffect(() => { load() }, [date]) // eslint-disable-line

  async function load() {
    setLoading(true)
    const [{ data: staff }, { data: jobs }, { data: routes }, { data: pdh }] = await Promise.all([
      supabase.from('staff').select('*').eq('is_active', true),
      supabase.from('jobs').select('*').eq('scheduled_date', date).eq('status', 'scheduled'),
      supabase.from('routes').select('*').eq('route_date', date),
      supabase.from('payroll_daily_hours').select('*').eq('date', date),
    ])

    const jobIds = (jobs||[]).map(j=>j.id)
    const [{ data: estimates }, { data: logs }] = jobIds.length ? await Promise.all([
      supabase.from('job_estimates').select('*').in('job_id', jobIds),
      supabase.from('time_logs').select('*').in('job_id', jobIds),
    ]) : [{data:[]},{data:[]}]

    // route+day -> staffIds, same pattern used for the weekly report
    const routeStaffMap = {}
    ;(routes||[]).filter(r=>r.label!=='__draft__').forEach(r => {
      try { routeStaffMap[r.label] = JSON.parse(r.notes||'{}').staffIds || [] } catch(e) { routeStaffMap[r.label] = [] }
    })

    const finalMap = {}
    ;(pdh||[]).forEach(p => { finalMap[p.staff_id] = p.hours })

    const summary = {}
    ;(staff||[]).forEach(s => { summary[s.id] = { staffId:s.id, name:s.name, calculated:0, approved:0, clocked:0, hasActivity:false } })

    // Calculated: scheduled hours, credited to every staff member on that job's route
    ;(jobs||[]).forEach(job => {
      const staffIds = routeStaffMap[job.route] || []
      staffIds.forEach(sid => {
        if (summary[sid]) { summary[sid].calculated += parseFloat(job.hours)||0; summary[sid].hasActivity = true }
      })
    })

    // Approved: estimates this specific person submitted and got approved
    ;(estimates||[]).forEach(est => {
      if (est.approved && summary[est.staff_id]) {
        summary[est.staff_id].approved += parseFloat(est.estimated_hours)||0
        summary[est.staff_id].hasActivity = true
      }
    })

    // Clocked: actual time from their own clock in/out
    ;(logs||[]).forEach(log => {
      if (log.clock_in && log.clock_out && summary[log.staff_id]) {
        summary[log.staff_id].clocked += (new Date(log.clock_out) - new Date(log.clock_in)) / 3600000
        summary[log.staff_id].hasActivity = true
      }
    })

    const result = Object.values(summary)
      .filter(s => s.hasActivity)
      .map(s => ({ ...s, finalHours: finalMap[s.staffId] != null ? String(finalMap[s.staffId]) : (s.clocked ? s.clocked.toFixed(1) : '') }))
      .sort((a,b) => a.name.localeCompare(b.name))

    setRows(result)
    setLoading(false)
  }

  function updateFinal(staffId, val) {
    setRows(prev => prev.map(r => r.staffId===staffId ? { ...r, finalHours: val } : r))
  }

  async function saveFinal(staffId) {
    const row = rows.find(r=>r.staffId===staffId)
    if (!row || row.finalHours === '') return
    setSaving(p=>({...p,[staffId]:true}))
    await supabase.from('payroll_daily_hours').upsert({
      staff_id: staffId, date, hours: parseFloat(row.finalHours)||0, updated_at: new Date().toISOString()
    }, { onConflict: 'staff_id,date' })
    setSaving(p=>({...p,[staffId]:false}))
  }

  return (
    <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', background:'#f8f9fa' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <div style={{ fontSize:13, fontWeight:700 }}>⏱️ Horas del día</div>
        <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{ padding:'6px 10px', border:'1px solid #e3e5e8', borderRadius:8, fontSize:12 }} />
      </div>
      <div style={{ fontSize:11, color:'#9aa0a6', marginBottom:14 }}>
        Lo que pongas en "Horas a pagar" es lo que se usa para Nómina — no los otros números.
      </div>

      {loading ? (
        <div style={{ textAlign:'center', color:'#9aa0a6', padding:30 }}>Cargando...</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign:'center', color:'#9aa0a6', padding:30 }}>Sin actividad registrada este día</div>
      ) : (
        <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e3e5e8', overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr 1fr 1fr 1.2fr', gap:8, padding:'10px 14px', background:'#f1f3f4', fontSize:10, fontWeight:700, color:'#80868b', textTransform:'uppercase' }}>
            <div>Empleada</div>
            <div>Calculado</div>
            <div>Aprobado</div>
            <div>Reloj</div>
            <div>Horas a pagar</div>
          </div>
          {rows.map(r => (
            <div key={r.staffId} style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr 1fr 1fr 1.2fr', gap:8, padding:'10px 14px', borderTop:'1px solid #f2f2f2', alignItems:'center' }}>
              <div style={{ fontSize:13, fontWeight:600 }}>{r.name}</div>
              <div style={{ fontSize:13, color:'#5f6368' }}>{r.calculated.toFixed(1)}h</div>
              <div style={{ fontSize:13, color:'#5f6368' }}>{r.approved ? r.approved.toFixed(1)+'h' : '—'}</div>
              <div style={{ fontSize:13, color:'#5f6368' }}>{r.clocked ? r.clocked.toFixed(1)+'h' : '—'}</div>
              <div style={{ display:'flex', gap:6 }}>
                <input type="number" step="0.1" min="0" value={r.finalHours} onChange={e=>updateFinal(r.staffId, e.target.value)}
                  style={{ width:60, padding:'6px 8px', border:'1px solid #e8547a', borderRadius:8, fontSize:13, fontWeight:700, color:'#e8547a' }} />
                <button onClick={()=>saveFinal(r.staffId)} disabled={saving[r.staffId]}
                  style={{ padding:'6px 10px', border:'none', borderRadius:8, background:'#1d9e75', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                  {saving[r.staffId] ? '...' : '💾'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function mondayOf(dateStr) {
  const d = new Date(dateStr+'T12:00:00')
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(d)
  monday.setDate(diff)
  return `${monday.getFullYear()}-${String(monday.getMonth()+1).padStart(2,'0')}-${String(monday.getDate()).padStart(2,'0')}`
}
function addDays(dateStr, n) {
  const d = new Date(dateStr+'T12:00:00')
  d.setDate(d.getDate()+n)
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}
function fmtDateShort(dateStr) {
  return new Date(dateStr+'T12:00:00').toLocaleDateString('es-MX',{weekday:'short',day:'numeric',month:'short'})
}
// Manual hours override helpers (timesheet_overrides table — clock data never touched)
function ovrKey(jobId, staffId) { return `job:${jobId}|staff:${staffId}` }
function parseHoursInput(txt) {
  const t = String(txt||'').trim().toLowerCase()
  if (!t) return null
  if (/^\d+:\d{1,2}$/.test(t)) { const [h,m] = t.split(':').map(Number); return h + m/60 }
  const hm = t.match(/(\d+(?:\.\d+)?)\s*h/), mm = t.match(/(\d+)\s*m/)
  if (hm || mm) return (hm ? parseFloat(hm[1]) : 0) + (mm ? parseInt(mm[1])/60 : 0)
  const n = parseFloat(t)
  return isNaN(n) ? null : n
}
function fmtHrs(h) {
  if (h == null) return ''
  const mins = Math.round(h*60), hh = Math.floor(mins/60), mm = mins%60
  if (hh === 0) return `${mm}min`
  return mm === 0 ? `${hh}h` : `${hh}h ${mm}min`
}
function hoursBetween(inIso, outIso) {
  if (!inIso || !outIso) return 0
  return (new Date(outIso) - new Date(inIso)) / 3600000
}

function WeeklySummaryPanel() {
  const [weekStart, setWeekStart] = useState(mondayOf(todayStr()))
  const [staffRows, setStaffRows] = useState([]) // [{staffId, name, days:{date:{jobs:[...], payrollHours}}, totalClocked, totalPaid}]
  const [loading, setLoading] = useState(true)
  const [ovr, setOvr] = useState({})       // ovrKey -> {hours, reason}
  const [drafts, setDrafts] = useState({}) // ovrKey -> {hours:'txt', reason:'txt'} while typing
  const [savingKey, setSavingKey] = useState(null)
  const [saveError, setSaveError] = useState('')
  const [openIds, setOpenIds] = useState({})   // staffId -> true when expanded
  const [linkedMsg, setLinkedMsg] = useState({}) // ovrKey -> 'names' after 🔗
  function toggleOpen(id) { setOpenIds(prev => ({ ...prev, [id]: !prev[id] })) }

  const weekDates = Array.from({length:7}, (_,i) => addDays(weekStart, i))
  const weekEnd = weekDates[6]

  useEffect(() => { load() }, [weekStart]) // eslint-disable-line

  async function load() {
    setLoading(true)
    const [{ data: staffData }, { data: jobs }, { data: routes }, { data: pdh }, { data: clients }] = await Promise.all([
      supabase.from('staff').select('*').eq('is_active', true),
      supabase.from('jobs').select('*').gte('scheduled_date', weekStart).lte('scheduled_date', weekEnd).eq('status','scheduled'),
      supabase.from('routes').select('*').gte('route_date', weekStart).lte('route_date', weekEnd),
      supabase.from('payroll_daily_hours').select('*').gte('date', weekStart).lte('date', weekEnd),
      supabase.from('clients').select('*'),
    ])

    const jobIds = (jobs||[]).map(j=>j.id)
    const { data: logs } = jobIds.length
      ? await supabase.from('time_logs').select('*').in('job_id', jobIds)
      : { data: [] }

    const { data: ovrRows } = await supabase.from('timesheet_overrides').select('*')
      .gte('date', weekStart).lte('date', weekEnd)
    const om = {}
    ;(ovrRows||[]).forEach(o => { om[o.row_key] = { hours: o.hours != null ? parseFloat(o.hours) : null, reason: o.reason || '' } })
    setOvr(om)
    setDrafts({})

    // route_date|label -> staffIds (+ who drove, for driver/passenger pay)
    const routeStaffMap = {}, routeDriverMap = {}
    ;(routes||[]).forEach(r => {
      try {
        const parsed = JSON.parse(r.notes||'{}')
        routeStaffMap[r.route_date+'|'+r.label] = parsed.staffIds || []
        routeDriverMap[r.route_date+'|'+r.label] = parsed.drivers || []
      } catch(e) { routeStaffMap[r.route_date+'|'+r.label] = []; routeDriverMap[r.route_date+'|'+r.label] = [] }
    })

    const staffMap = {}
    ;(staffData||[]).forEach(s => { staffMap[s.id] = { staffId:s.id, name:s.name, profile:s, days:{}, totalClocked:0, totalPaid:0 } })

    ;(jobs||[]).forEach(job => {
      const staffIds = routeStaffMap[job.scheduled_date+'|'+job.route] || []
      const client = (clients||[]).find(c=>c.id===job.client_id)
      staffIds.forEach(sid => {
        if (!staffMap[sid]) return
        if (!staffMap[sid].days[job.scheduled_date]) staffMap[sid].days[job.scheduled_date] = { jobs: [], payrollHours: null }
        const log = (logs||[]).find(l=>l.job_id===job.id && l.staff_id===sid)
        const teammateIds = staffIds.filter(id=>id!==sid && (staffData||[]).some(s=>s.id===id))
        const teammates = teammateIds.map(id=>(staffData||[]).find(s=>s.id===id)?.name)
        staffMap[sid].days[job.scheduled_date].jobs.push({
          jobId: job.id,
          staffId: sid,
          drove: (routeDriverMap[job.scheduled_date+'|'+job.route] || []).includes(sid),
          date: job.scheduled_date,
          clientName: client?.nickname||client?.name||'?',
          address: client?.address ? client.address+(client.city?', '+client.city:'') : '',
          startTime: job.start_time,
          clockIn: log?.clock_in||null,
          clockOut: log?.clock_out||null,
          teammates,
          teammateIds,
        })
      })
    })

    ;(pdh||[]).forEach(p => {
      if (staffMap[p.staff_id] && staffMap[p.staff_id].days[p.date]) {
        staffMap[p.staff_id].days[p.date].payrollHours = p.hours
      }
    })

    Object.values(staffMap).forEach(s => {
      Object.values(s.days).forEach(d => {
        d.jobs.sort((a,b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))
      })
    })

    const result = Object.values(staffMap)
      .filter(s => Object.keys(s.days).length > 0)
      .map(s => {
        let totalClocked = 0, totalPaid = 0
        Object.values(s.days).forEach(d => {
          d.jobs.forEach(j => { totalClocked += hoursBetween(j.clockIn, j.clockOut) })
          if (d.payrollHours != null) totalPaid += parseFloat(d.payrollHours) || 0
        })
        return { ...s, totalClocked, totalPaid }
      })
      .sort((a,b) => a.name.localeCompare(b.name))

    setStaffRows(result)
    setLoading(false)
  }

  function clockedHrs(j) { return hoursBetween(j.clockIn, j.clockOut) }
  function paidHrs(j) {
    const o = ovr[ovrKey(j.jobId, j.staffId)]
    return (o && o.hours != null) ? o.hours : clockedHrs(j)
  }
  function adjustedTotal(s) {
    let t = 0
    Object.values(s.days).forEach(d => d.jobs.forEach(j => { t += paidHrs(j) }))
    return t
  }
  // Pay from the staff profile — same rules as Nómina (tips/miles/flat not included here)
  function rateFor(p, drove) {
    if (p.pay_type_v2 === 'driver_passenger') {
      return drove ? (parseFloat(p.driver_rate)||parseFloat(p.pay_rate)||0) : (parseFloat(p.passenger_rate)||parseFloat(p.pay_rate)||0)
    }
    return parseFloat(p.pay_rate)||0
  }
  function isFixedPay(p) { return p.pay_type !== 'hourly' && p.pay_type_v2 !== 'driver_passenger' && p.pay_type_v2 !== 'base_mileage' }
  function jobPay(s, j) { return isFixedPay(s.profile) ? null : paidHrs(j) * rateFor(s.profile, j.drove) }
  function payTotal(s) {
    if (isFixedPay(s.profile)) return parseFloat(s.profile.pay_rate)||0
    let t = 0
    Object.values(s.days).forEach(d => d.jobs.forEach(j => { t += jobPay(s, j) }))
    return t
  }
  function rateLabel(p) {
    if (p.pay_type_v2 === 'driver_passenger') return `Conductora $${rateFor(p,true)}/hr · Pasajera $${rateFor(p,false)}/hr`
    if (isFixedPay(p)) return `Fijo $${parseFloat(p.pay_rate)||0}`
    return `$${parseFloat(p.pay_rate)||0}/hr`
  }
  function adjustedCount(s) {
    let n = 0
    Object.values(s.days).forEach(d => d.jobs.forEach(j => { if (ovr[ovrKey(j.jobId, j.staffId)]) n++ }))
    return n
  }
  function draftVal(key, field) {
    if (drafts[key]?.[field] !== undefined) return drafts[key][field]
    const o = ovr[key]
    if (!o) return ''
    return field === 'hours' ? fmtHrs(o.hours) : (o.reason || '')
  }
  function setDraft(key, field, val) {
    setDrafts(prev => ({ ...prev, [key]: { ...prev[key], [field]: val } }))
  }
  // Writes (or clears) one adjustment row. Never touches time_logs.
  async function writeOvr(key, date, staffName, clientName, hours, reason) {
    if (hours == null && !reason) {
      const { error } = await supabase.from('timesheet_overrides').delete().eq('row_key', key)
      return error
    }
    const { error } = await supabase.from('timesheet_overrides').upsert({
      row_key: key, date, staff_name: staffName, client_name: clientName,
      hours, reason: reason || null, updated_at: new Date().toISOString(),
    }, { onConflict: 'row_key' })
    return error
  }
  function currentInput(key) {
    const d = drafts[key] || {}
    const hoursTxt = d.hours !== undefined ? d.hours : (ovr[key]?.hours != null ? String(ovr[key].hours) : '')
    const reason = (d.reason !== undefined ? d.reason : (ovr[key]?.reason || '')).trim()
    return { hours: parseHoursInput(hoursTxt), reason }
  }
  function applyLocal(keys, hours, reason) {
    setOvr(prev => {
      const n = { ...prev }
      keys.forEach(k => { if (hours == null && !reason) delete n[k]; else n[k] = { hours, reason } })
      return n
    })
    setDrafts(prev => { const n = { ...prev }; keys.forEach(k => delete n[k]); return n })
  }
  async function saveOvr(j, staffName) {
    const key = ovrKey(j.jobId, j.staffId)
    if (!drafts[key]) return
    const { hours, reason } = currentInput(key)
    setSavingKey(key); setSaveError('')
    const error = await writeOvr(key, j.date, staffName, j.clientName, hours, reason)
    if (error) setSaveError('No se guardó: ' + error.message)
    else applyLocal([key], hours, reason)
    setSavingKey(null)
  }
  // 🔗 Copy this house's adjustment to the partners on the same job
  async function linkToPartners(j, staffName) {
    const key = ovrKey(j.jobId, j.staffId)
    const { hours, reason } = currentInput(key)
    if (hours == null && !reason) return
    setSavingKey(key); setSaveError('')
    const targets = [{ key, name: staffName }, ...j.teammateIds.map((tid, i) => ({ key: ovrKey(j.jobId, tid), name: j.teammates[i] }))]
    for (const t of targets) {
      const error = await writeOvr(t.key, j.date, t.name, j.clientName, hours, reason)
      if (error) { setSaveError('No se guardó: ' + error.message); setSavingKey(null); return }
    }
    applyLocal(targets.map(t => t.key), hours, reason)
    setLinkedMsg(prev => ({ ...prev, [key]: j.teammates.join(', ') }))
    setTimeout(() => setLinkedMsg(prev => { const n = { ...prev }; delete n[key]; return n }), 3000)
    setSavingKey(null)
  }

  function printWeek() {
    const rowsHtml = staffRows.map(s => {
      const dayBlocks = weekDates.map(dk => {
        const day = s.days[dk]
        if (!day || day.jobs.length===0) return ''
        const jobsHtml = day.jobs.map(j => {
          const o = ovr[ovrKey(j.jobId, j.staffId)]
          const noClock = !(j.clockIn && j.clockOut)
          return `
          <tr style="${o ? 'background:#fdf0f3;' : ''}">
            <td>${j.startTime||''}</td>
            <td>${j.clientName}</td>
            <td>${j.teammates.join(', ')||'—'}</td>
            <td>${fmtTime(j.clockIn)||'—'}</td>
            <td>${fmtTime(j.clockOut)||'—'}</td>
            <td>${noClock ? '—' : fmtHrs(clockedHrs(j))}</td>
            <td style="font-weight:700;color:#e8547a;">${o && o.hours != null ? fmtHrs(o.hours) : (noClock ? '' : fmtHrs(clockedHrs(j)))}</td>
            <td style="color:#5f6368;">${o?.reason || ''}</td>
            <td style="text-align:right;">${jobPay(s,j)!=null ? '$'+jobPay(s,j).toFixed(2) : ''}${s.profile.pay_type_v2==='driver_passenger' ? (j.drove?' 🚗':'') : ''}</td>
          </tr>`}).join('')
        return `
          <div style="font-size:11px;font-weight:700;color:#e8547a;margin:10px 0 4px;">${fmtDateShort(dk)}</div>
          <table style="width:100%;border-collapse:collapse;margin-bottom:6px;">
            <thead><tr style="background:#fdeef3;font-size:9px;text-transform:uppercase;">
              <th style="padding:5px 8px;text-align:left;">Hora</th>
              <th style="padding:5px 8px;text-align:left;">Cliente</th>
              <th style="padding:5px 8px;text-align:left;">Con quién</th>
              <th style="padding:5px 8px;text-align:left;">Entrada</th>
              <th style="padding:5px 8px;text-align:left;">Salida</th>
              <th style="padding:5px 8px;text-align:left;">Reloj</th>
              <th style="padding:5px 8px;text-align:left;">Pagar</th>
              <th style="padding:5px 8px;text-align:left;">Nota</th>
              <th style="padding:5px 8px;text-align:right;">Pago</th>
            </tr></thead>
            <tbody style="font-size:11px;">${jobsHtml}</tbody>
          </table>`
      }).join('')
      return `
        <div style="page-break-inside:avoid;margin-bottom:26px;border:2px solid #e8547a;border-radius:10px;overflow:hidden;">
          <div style="background:#e8547a;color:#fff;padding:10px 16px;font-weight:700;font-size:14px;">${s.name}</div>
          <div style="padding:12px 16px;">
            ${dayBlocks}
            <div style="display:flex;justify-content:space-between;margin-top:10px;padding-top:10px;border-top:1.5px dashed #bdc1c6;font-size:12px;">
              <div>Total con reloj: <strong>${fmtHrs(s.totalClocked)||'0h'}</strong></div>
              <div>Total a pagar: <strong style="color:#e8547a;">${fmtHrs(adjustedTotal(s))||'0h'}</strong>${adjustedCount(s)>0?` <span style="color:#80868b;font-size:10px;">(${adjustedCount(s)} ajustada${adjustedCount(s)>1?'s':''})</span>`:''}</div>
              <div>Nómina tiene: <strong>${s.totalPaid>0?s.totalPaid.toFixed(1)+'h':'—'}</strong></div>
            </div>
            <div style="margin-top:10px;font-size:13px;">Monto a pagar: <strong style="color:#e8547a;">$${payTotal(s).toFixed(2)}</strong></div>
          </div>
        </div>`
    }).join('')

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Resumen semanal</title>
    <style>
      * { box-sizing:border-box; font-family:Arial,sans-serif; }
      body { padding:30px; color:#202124; }
      @media print { body { padding:16px; } }
    </style></head><body>
    <div style="text-align:center;margin-bottom:20px;">
      <div style="font-size:20px;font-weight:800;">🐈 Kitty's Cleaning Services</div>
      <div style="font-size:13px;color:#5f6368;">Resumen semanal — ${fmtDateShort(weekStart)} a ${fmtDateShort(weekEnd)}</div>
    </div>
    ${rowsHtml}
    <script>window.onload=()=>window.print()</script>
    </body></html>`
    const win = window.open('','_blank')
    win.document.write(html)
    win.document.close()
  }

  return (
    <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', background:'#f8f9fa' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <div style={{ fontSize:13, fontWeight:700 }}>📅 Resumen semanal por empleada</div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={()=>setWeekStart(addDays(weekStart,-7))} style={{ border:'1px solid #e3e5e8', background:'#fff', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:12 }}>←</button>
          <div style={{ fontSize:12, color:'#5f6368', fontWeight:600 }}>{fmtDateShort(weekStart)} — {fmtDateShort(weekEnd)}</div>
          <button onClick={()=>setWeekStart(addDays(weekStart,7))} style={{ border:'1px solid #e3e5e8', background:'#fff', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:12 }}>→</button>
          <button onClick={printWeek} style={{ border:'none', background:'#e8547a', color:'#fff', borderRadius:8, padding:'7px 14px', cursor:'pointer', fontSize:12, fontWeight:700 }}>🖨️ Imprimir</button>
        </div>
      </div>
      <div style={{ fontSize:11, color:'#9aa0a6', marginBottom:14 }}>
        Casas con nombre y dirección, hora de entrada/salida por reloj, y lo que Nómina tiene registrado para pagar — para tu chequeo físico.
        {' '}Escribe en <b style={{color:'#e8547a'}}>Pagar</b> para ajustar horas (3.5, 3h 30min o 3:30). Se guarda al salir del campo.
      </div>
      {saveError && <div style={{ fontSize:12, color:'#fff', background:'#e8547a', padding:'6px 10px', borderRadius:8, marginBottom:10 }}>{saveError}</div>}

      {loading ? (
        <div style={{ textAlign:'center', color:'#9aa0a6', padding:30 }}>Cargando...</div>
      ) : staffRows.length === 0 ? (
        <div style={{ textAlign:'center', color:'#9aa0a6', padding:30 }}>Sin actividad esta semana</div>
      ) : staffRows.map(s => (
        <div key={s.staffId} style={{ background:'#fff', borderRadius:12, border:'1px solid #e3e5e8', marginBottom:14, overflow:'hidden' }}>
          <div onClick={()=>toggleOpen(s.staffId)}
            style={{ background:'#fdeef3', padding:'10px 14px', fontWeight:700, fontSize:13, color:'#c03060', cursor:'pointer', display:'flex', alignItems:'center', gap:8, userSelect:'none' }}>
            <span style={{ fontSize:11, width:12 }}>{openIds[s.staffId] ? '▼' : '▶'}</span>
            {s.name}
          </div>
          {openIds[s.staffId] && <div style={{ padding:'10px 14px' }}>
            {weekDates.map(dk => {
              const day = s.days[dk]
              if (!day || day.jobs.length===0) return null
              return (
                <div key={dk} style={{ marginBottom:10 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#e8547a', marginBottom:4 }}>{fmtDateShort(dk)}</div>
                  {day.jobs.map((j,i) => {
                    const key = ovrKey(j.jobId, j.staffId)
                    const o = ovr[key]
                    return (
                    <div key={i} style={{ display:'grid', gridTemplateColumns:'1.3fr 0.9fr 0.6fr 0.6fr 1.2fr', gap:8, padding:'5px 4px', borderBottom:'1px solid #f1f3f4', fontSize:12, alignItems:'start', background: o ? '#fdf0f3' : 'transparent', borderRadius: o ? 6 : 0 }}>
                      <div>
                        <div style={{ fontWeight:600 }}>{j.clientName}</div>
                        <div style={{ fontSize:10, color:'#9aa0a6' }}>{j.address}</div>
                        {j.teammates.length>0 && <div style={{ fontSize:10, color:'#185fa5', marginTop:1 }}>👥 {j.teammates.join(', ')}</div>}
                        {jobPay(s,j)!=null && jobPay(s,j)>0 && <div style={{ fontSize:10, color:'#80868b', marginTop:1 }}>${jobPay(s,j).toFixed(2)}{s.profile.pay_type_v2==='driver_passenger' && j.drove ? ' · 🚗 conductora' : ''}</div>}
                      </div>
                      <div style={{ color:'#5f6368' }}>{fmtTime(j.clockIn)||'—'} → {fmtTime(j.clockOut)||'—'}</div>
                      <div style={{ textAlign:'right', color:'#1d9e75', fontWeight:600 }}>
                        {j.clockIn&&j.clockOut ? fmtDuration(j.clockIn,j.clockOut) : '—'}
                      </div>
                      <div>
                        <input value={draftVal(key,'hours')} placeholder="pagar"
                          onChange={e=>setDraft(key,'hours',e.target.value)}
                          onBlur={()=>saveOvr(j, s.name)}
                          onKeyDown={e=>{ if(e.key==='Enter') e.target.blur() }}
                          style={{ width:'100%', padding:'4px 6px', border:`1px solid ${o?'#e8547a':'#e3e5e8'}`, borderRadius:6, fontSize:12, fontWeight:o?700:400, color:'#e8547a', background:'#fff' }} />
                        {(o || drafts[key]) && j.teammateIds.length>0 && (
                          <button onMouseDown={e=>e.preventDefault()} onClick={()=>linkToPartners(j, s.name)}
                            title={`Aplicar también a ${j.teammates.join(', ')}`}
                            style={{ marginTop:3, border:'1px solid #e3e5e8', background:'#fff', borderRadius:6, padding:'1px 6px', fontSize:11, cursor:'pointer' }}>
                            🔗 <span style={{ fontSize:9, color:'#185fa5' }}>{j.teammates.map(n=>n.split(' ')[0]).join(', ')}</span>
                          </button>
                        )}
                        {linkedMsg[key] && <div style={{ fontSize:9, color:'#1d9e75' }}>✓ aplicado a {linkedMsg[key]}</div>}
                        {savingKey===key && <div style={{ fontSize:9, color:'#9aa0a6' }}>guardando...</div>}
                      </div>
                      <input value={draftVal(key,'reason')} placeholder="razón (opcional)"
                        onChange={e=>setDraft(key,'reason',e.target.value)}
                        onBlur={()=>saveOvr(j, s.name)}
                        onKeyDown={e=>{ if(e.key==='Enter') e.target.blur() }}
                        style={{ width:'100%', padding:'4px 6px', border:'1px solid #e3e5e8', borderRadius:6, fontSize:11, color:'#5f6368', background:'#fff' }} />
                    </div>
                    )
                  })}
                </div>
              )
            })}
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:8, paddingTop:8, borderTop:'1.5px dashed #eee', fontSize:12 }}>
              <div style={{ color:'#5f6368' }}>Total con reloj: <strong>{fmtHrs(s.totalClocked)||'0h'}</strong></div>
              <div style={{ color:'#5f6368' }}>Total a pagar: <strong style={{color:'#e8547a'}}>{fmtHrs(adjustedTotal(s))||'0h'}</strong>{adjustedCount(s)>0 && <span style={{fontSize:10,color:'#9aa0a6'}}> ({adjustedCount(s)} ajustada{adjustedCount(s)>1?'s':''})</span>}</div>
              <div style={{ color:'#5f6368' }}>Nómina tiene: <strong>{s.totalPaid>0?s.totalPaid.toFixed(1)+'h':'—'}</strong></div>
            </div>
            <div style={{ marginTop:6, fontSize:12, color:'#5f6368', textAlign:'right' }}>
              Pago: <strong style={{ color:'#e8547a', fontSize:14 }}>${payTotal(s).toFixed(2)}</strong>
            </div>
          </div>}
        </div>
      ))}
    </div>
  )
}
